import { authenticationService } from "@authentication/services/AuthenticationService";
import { goTo } from "@history";
import { AuthenticationRoutes } from "@routes/urls";
import PropTypes from "prop-types";
import React, { Component } from "react";
import Overridable from "react-overridable";
import SignupWithLocalAccountLayout from "./SignupWithLocalAccountLayout";
import { validateForm, prepareSubmitData } from "./signupValidation";
import { handleSignupError } from "./signupErrorHandler";
import { AuditService } from "@api/audit/audit";
import { WelcomeManager } from "@utils/WelcomeGuideUtils";

const BUTTON_TEXT = {
  SUBMITTING: "Creating Account...",
  DEFAULT: "Create Account",
};

const SUCCESS_MESSAGES = {
  EMAIL_VERIFICATION:
    "Account created! Please check your email to verify your account before logging in.",
  DEFAULT:
    "Account created successfully! You can now log in with your credentials.",
};

const REDIRECT_DELAYS = {
  EMAIL_VERIFICATION: 5000,
  DEFAULT: 3000,
};

class SignupWithLocalAccount extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: {
        email: "",
        username: "",
        full_name: "",
        password: "",
        password_confirm: "",
        member_types: [], // Add member types array
      },
      hasSuccess: false,
      successMessage: "",
      isSubmitting: false,
      hasError: false,
      errorHeader: "",
      errorMessage: "",
    };
  }

  get buttons() {
    const { isSubmitting } = this.state;
    return [
      {
        name: "signup",
        content: isSubmitting ? BUTTON_TEXT.SUBMITTING : BUTTON_TEXT.DEFAULT,
        primary: true,
        type: "submit",
        fluid: true,
        loading: isSubmitting,
        disabled: isSubmitting,
      },
    ];
  }

  // Handle member type changes
  handleMemberTypeChange = (selectedTypes) => {
    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        member_types: selectedTypes,
      },
    }));
  };

  // Reset error state
  clearErrors = () => {
    this.setState({
      hasError: false,
      errorHeader: "",
      errorMessage: "",
    });
  };

  // Set error state
  setError = (errorHeader, errorMessage) => {
    this.setState({
      hasError: true,
      errorHeader,
      errorMessage,
      hasSuccess: false,
    });
  };

  // Set success state
  setSuccess = (successMessage) => {
    this.setState({
      hasSuccess: true,
      hasError: false,
      successMessage,
    });
  };

  // Handle registration success
  handleRegistrationSuccess = async (response) => {
    const responseData = response.data || response;

    // Determine success message
    let successMessage;
    if (responseData.needs_email_verification) {
      successMessage = SUCCESS_MESSAGES.EMAIL_VERIFICATION;
    } else {
      successMessage = SUCCESS_MESSAGES.DEFAULT;
    }

    this.setSuccess(successMessage);

    // ---- AUDIT LOG INSERTION ----
    const fullName = responseData.full_name || "Unknown User";
    const username = responseData.username || "Unknown Username";
    const email = responseData.email || "Unknown Email";
    const role = responseData.user_type_code || "patron"; // default if null

    const details = `The user ${fullName} (${role}) with username ${username} and email ${email} has signed up at ${new Date().toLocaleString()}.`;

    await AuditService.api.createLog({
      action: "Signup Account",
      details,
      created_by: `${fullName} Role : (${role})`,
    });
    // -------------------------------

    // Mark user as newly registered for welcome modal (if it fails, continue with normal flow)
    try {
      if (responseData.user) {
        WelcomeManager.markAsNewlyRegistered(responseData.user);
      }
    } catch (error) {
      // Continue with normal flow even if welcome manager fails
    }

    this.handleRedirect(responseData);
  };

  handleRedirect = (responseData) => {
    const delay = responseData.needs_email_verification
      ? REDIRECT_DELAYS.EMAIL_VERIFICATION
      : REDIRECT_DELAYS.DEFAULT;

    setTimeout(() => {
      if (responseData.needs_email_verification) {
        goTo(`${AuthenticationRoutes.login}?message=verify-email`);
      } else {
        goTo(AuthenticationRoutes.login);
      }
    }, delay);
  };

  onSubmit = async (values, actions) => {
    this.setState({ isSubmitting: true });

    try {
      // Include member_types from state in validation values
      const valuesWithMemberTypes = {
        ...values,
        member_types: this.state.data.member_types,
      };

      const validationErrors = validateForm(valuesWithMemberTypes);
      if (Object.keys(validationErrors).length > 0) {
        actions.setErrors(validationErrors);
        this.setState({ isSubmitting: false });
        return;
      }

      const submitData = prepareSubmitData(valuesWithMemberTypes);
      this.clearErrors();

      // Register user
      const response = await authenticationService.registerWithLocalAccount(
        submitData
      );

      // Handle success scenario
      await this.handleRegistrationSuccess(response);
    } catch (error) {
      const { errorHeader, errorMessage } = handleSignupError(error, actions);
      this.setError(errorHeader, errorMessage);
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  render() {
    const {
      hasError: propsHasError,
      errorHeader: propsErrorHeader,
      errorMessage: propsErrorMessage,
    } = this.props;
    const {
      data,
      hasSuccess,
      successMessage,
      isSubmitting,
      hasError: stateHasError,
      errorHeader: stateErrorHeader,
      errorMessage: stateErrorMessage,
    } = this.state;

    const displayError = stateHasError || propsHasError;
    const displayErrorHeader = stateErrorHeader || propsErrorHeader;
    const displayErrorMessage = stateErrorMessage || propsErrorMessage;

    return (
      <SignupWithLocalAccountLayout
        {...this.props}
        data={data}
        buttons={this.buttons}
        onSubmit={this.onSubmit}
        errorHeader={displayErrorHeader}
        errorMessage={displayErrorMessage}
        hasError={displayError}
        hasSuccess={hasSuccess}
        successMessage={successMessage}
        isSubmitting={isSubmitting}
        // Add member type props
        memberTypes={data.member_types}
        onMemberTypeChange={this.handleMemberTypeChange}
      />
    );
  }
}

SignupWithLocalAccount.propTypes = {
  fetchUserProfile: PropTypes.func.isRequired,
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
  errorHeader: PropTypes.string,
};

SignupWithLocalAccount.defaultProps = {
  hasError: false,
  errorHeader: "Registration Error",
  errorMessage: "",
};

export default Overridable.component(
  "SignupWithLocalAccount",
  SignupWithLocalAccount
);
